import { supabase } from '@/lib/supabase'
import { uploadPublicFile } from '@/modules/upload/services/uploadService'
import {
  COMMUNITY_CUSTOM_EMOJI_LIMITS,
  COMMUNITY_CUSTOM_EMOJI_CANONICAL_PX,
  validateCommunityCustomEmojiFile,
} from '../utils/communityCustomEmojis'
import {
  COMMUNITY_CUSTOM_EMOJI_STORAGE_BUCKET,
  COMMUNITY_CUSTOM_EMOJI_STORAGE_PREFIX,
} from '../utils/communityEmojiTokens'

export type CommunityCustomEmoji = {
  id: string
  label: string | null
  storageBucket: string | null
  storagePath: string | null
  contentType: string | null
  sizeBytes: number | null
  createdAt: string
  createdBy: string | null
}

const TABLE = 'community_custom_emojis'
const STORAGE_PREFIX_WITH_SLASH = `${COMMUNITY_CUSTOM_EMOJI_STORAGE_PREFIX}/`

let cached: CommunityCustomEmoji[] | null = null
let cachedPromise: Promise<CommunityCustomEmoji[]> | null = null

function normalizeText(input: unknown): string {
  return String(input ?? '').trim()
}

function normalizeNullableText(input: unknown): string | null {
  const value = normalizeText(input)
  return value ? value : null
}

function normalizeNullableNumber(input: unknown): number | null {
  const n = typeof input === 'number' ? input : Number(input)
  return Number.isFinite(n) ? n : null
}

function mapRow(row: any): CommunityCustomEmoji {
  return {
    id: normalizeText(row?.id),
    label: normalizeNullableText(row?.label),
    storageBucket: normalizeNullableText(row?.storage_bucket),
    storagePath: normalizeNullableText(row?.storage_path),
    contentType: normalizeNullableText(row?.content_type),
    sizeBytes: normalizeNullableNumber(row?.size_bytes),
    createdAt: normalizeText(row?.created_at),
    createdBy: normalizeNullableText(row?.created_by),
  }
}

export function invalidateCommunityCustomEmojisCache() {
  cached = null
  cachedPromise = null
}

export async function fetchCommunityCustomEmojis(): Promise<CommunityCustomEmoji[]> {
  try {
    const { data, error } = await supabase
      .from(TABLE as any)
      .select(
        'id,label,storage_bucket,storage_path,content_type,size_bytes,created_at,created_by',
      )
      .order('created_at', { ascending: true })

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[CommunityCustomEmoji] fetch error', error)
      return []
    }

    return Array.isArray(data) ? data.map(mapRow).filter((x) => !!x.id) : []
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[CommunityCustomEmoji] fetch exception', err)
    return []
  }
}

export async function getCommunityCustomEmojisCached(): Promise<CommunityCustomEmoji[]> {
  if (cached) return cached
  if (cachedPromise) return cachedPromise

  cachedPromise = (async () => {
    const list = await fetchCommunityCustomEmojis()
    cached = list
    cachedPromise = null
    return list
  })()

  return cachedPromise
}

async function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Falha ao ler imagem.'))
    }
    img.src = url
  })
}

async function toBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return await new Promise((resolve) => {
    try {
      if (type === 'image/jpeg') {
        canvas.toBlob((blob) => resolve(blob), type, 0.92)
        return
      }
      canvas.toBlob((blob) => resolve(blob), type)
    } catch {
      resolve(null)
    }
  })
}

async function maybeNormalizeCustomEmojiFile(file: File): Promise<File> {
  if (typeof document === 'undefined') return file
  const type = normalizeText(file.type).toLowerCase()
  if (!type || type === 'image/gif') return file

  try {
    const img = await loadImageFromFile(file)
    const width = Number(img.naturalWidth || img.width || 0)
    const height = Number(img.naturalHeight || img.height || 0)
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return file
    }

    const target = COMMUNITY_CUSTOM_EMOJI_CANONICAL_PX
    const needsResize = width !== height || width > target || height > target
    if (!needsResize) return file

    const canvas = document.createElement('canvas')
    canvas.width = target
    canvas.height = target
    const ctx = canvas.getContext('2d')
    if (!ctx) return file

    if (type === 'image/jpeg') {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, target, target)
    }

    const scale = Math.min(target / width, target / height)
    const drawW = Math.round(width * scale)
    const drawH = Math.round(height * scale)
    const dx = Math.round((target - drawW) / 2)
    const dy = Math.round((target - drawH) / 2)
    ctx.drawImage(img, dx, dy, drawW, drawH)

    const blob = await toBlob(canvas, type)
    if (!blob) return file
    const next = new File([blob], file.name, { type: blob.type || file.type })
    if (next.size <= COMMUNITY_CUSTOM_EMOJI_LIMITS.maxBytes) return next
    return file
  } catch {
    return file
  }
}

function deriveCustomEmojiIdFromStoragePath(path: string): string {
  const normalized = normalizeText(path)
  if (!normalized) return ''
  if (normalized.startsWith(STORAGE_PREFIX_WITH_SLASH)) {
    return normalized.slice(STORAGE_PREFIX_WITH_SLASH.length)
  }
  return normalized.split('/').filter(Boolean).pop() || normalized
}

export async function addCommunityCustomEmoji(
  file: File,
): Promise<{ ok: true; emoji: CommunityCustomEmoji } | { ok: false; reason: string }> {
  const validation = validateCommunityCustomEmojiFile(file)
  if (!validation.ok) return { ok: false, reason: validation.reason }

  const normalizedFile = await maybeNormalizeCustomEmojiFile(file)
  if (normalizedFile.size > COMMUNITY_CUSTOM_EMOJI_LIMITS.maxBytes) {
    return { ok: false, reason: 'Limite excedido (50 KB por emoji).' }
  }

  let uploaded: { url: string; bucket: string; path: string } | null = null
  try {
    uploaded = await uploadPublicFile(
      normalizedFile,
      COMMUNITY_CUSTOM_EMOJI_STORAGE_PREFIX,
      [COMMUNITY_CUSTOM_EMOJI_STORAGE_BUCKET],
    )
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[CommunityCustomEmoji] upload error', err)
    return { ok: false, reason: 'Falha ao enviar o emoji. Tente novamente.' }
  }

  const id = deriveCustomEmojiIdFromStoragePath(uploaded.path)
  if (!id) {
    return { ok: false, reason: 'Falha ao gerar identificador do emoji.' }
  }

  const label = normalizeNullableText(file?.name?.replace(/\.[^/.]+$/, ''))

  try {
    const { data, error } = await supabase
      .from(TABLE as any)
      .insert({
        id,
        label,
        storage_bucket: uploaded.bucket,
        storage_path: uploaded.path,
        content_type: normalizedFile.type || null,
        size_bytes:
          typeof normalizedFile.size === 'number' ? normalizedFile.size : null,
      } as any)
      .select(
        'id,label,storage_bucket,storage_path,content_type,size_bytes,created_at,created_by',
      )
      .single()

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[CommunityCustomEmoji] insert error', error)
      return { ok: false, reason: 'Falha ao registrar o emoji no banco.' }
    }

    const emoji = mapRow(data)
    invalidateCommunityCustomEmojisCache()
    return { ok: true, emoji }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[CommunityCustomEmoji] insert exception', err)
    return { ok: false, reason: 'Falha ao registrar o emoji no banco.' }
  }
}

export async function deleteCommunityCustomEmoji(
  emoji: Pick<CommunityCustomEmoji, 'id' | 'storageBucket' | 'storagePath'>,
): Promise<{ ok: true } | { ok: false; reason: string }> {
  const id = normalizeText(emoji?.id)
  if (!id) return { ok: false, reason: 'Emoji inválido.' }

  try {
    const { error } = await supabase.from(TABLE as any).delete().eq('id', id)
    if (error) {
      // eslint-disable-next-line no-console
      console.error('[CommunityCustomEmoji] delete error', error)
      return { ok: false, reason: 'Sem permissão para deletar este emoji.' }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[CommunityCustomEmoji] delete exception', err)
    return { ok: false, reason: 'Falha ao deletar emoji.' }
  }

  const bucket = normalizeNullableText(emoji?.storageBucket) || COMMUNITY_CUSTOM_EMOJI_STORAGE_BUCKET
  const path =
    normalizeNullableText(emoji?.storagePath) || `${COMMUNITY_CUSTOM_EMOJI_STORAGE_PREFIX}/${id}`

  if (bucket && path) {
    try {
      const { error } = await supabase.storage.from(bucket).remove([path])
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[CommunityCustomEmoji] storage delete error', error)
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[CommunityCustomEmoji] storage delete exception', err)
    }
  }

  invalidateCommunityCustomEmojisCache()
  return { ok: true }
}
